import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import {
  MessageTypeWebSocket,
  type BotAssignmentsUserAddedWebSocket,
  type BotAssignmentsUserAssignedWebSocket,
} from "@/Modules/Chat/Models/WebSocket";

import { useUserStore, type UserStore } from "@/Modules/User/Store";
import { usePatientsPoolStore, type PatientsPoolStore } from "@/Modules/PatientsPool/Store";
import useAppStore, { type AppStore } from "@/Modules/App/Store";
import PatientPoolTransformer from "@/Modules/Transformers/PatientPool";

import { hasValue, isObject } from "@/Utils/Objects";

import i18n from "@/Plugins/i18n";

export interface ExtraParamsAssignmentsUserAdded {
  patientsPoolStore: PatientsPoolStore;
  userStore: UserStore;
  appStore: AppStore;
}

export const isAssignmentsUserAddedType = (
  value: unknown
): value is BotAssignmentsUserAddedWebSocket | BotAssignmentsUserAssignedWebSocket => {
  return (
    isObject(value) &&
    (value.type === MessageTypeWebSocket.ASSIGNMENTS_USER_ADDED ||
      value.type === MessageTypeWebSocket.ASSIGNMENTS_USER_ASSIGNED)
  );
};

export const assignmentsUserAddedHandler = (
  message: BotAssignmentsUserAddedWebSocket | BotAssignmentsUserAssignedWebSocket,
  {
    patientsPoolStore = usePatientsPoolStore(),
    userStore = useUserStore(),
    appStore = useAppStore(),
  }: ExtraParamsAssignmentsUserAdded
): Promise<undefined> => {
  if (!hasValue(userStore.user)) return Promise.resolve(undefined);

  if (message.type === MessageTypeWebSocket.ASSIGNMENTS_USER_ADDED) {
    const t = i18n.global.t;

    patientsPoolStore.addPatient(PatientPoolTransformer.toDomain(message.data));

    appStore.notificate({
      title: t("browser-notifications.new_patient"),
      body: t("browser-notifications.pending_patient", { patient: message.data.customer_username }),
    });
  } else if (message.type === MessageTypeWebSocket.ASSIGNMENTS_USER_ASSIGNED) {
    patientsPoolStore.removePatient(message.data.customer_uuid);
  }

  return Promise.resolve(undefined);
};

export default {
  isType: isAssignmentsUserAddedType,
  handler: assignmentsUserAddedHandler,
} as EventFilter<ExtraParamsAssignmentsUserAdded>;
